// NotesApp source file: list.component.ts

import { NoteDisplayTitlePipe } from '../../../_util/note-display-title.util';
import type { PrivateTagIndex } from '../logic/filter.logic';
import { isPrivateTag } from '../../../_util/tag-scope.util';
import {
  ComponentFactoryResolver,
  Input,
  Output,
  EventEmitter,
  Injector,
  ApplicationRef,
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewContainerRef,
  computed,
  inject,
  HostListener,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { filter, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { NoteSizeService } from '../../../_service/note-size.service';
import { formatPersistedNoteSize } from '../../../_util/note-size.util';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Popover, PopoverModule } from 'primeng/popover';

// Notes-App Services
import {
  Note,
  Project,
  Status,
  NoteType,
  DataService,
  Tag,
} from '../../../_service/data.service';
import { Space } from '../../../_service/space.service';
import { NoteTagEditorComponent } from '../note-tag-editor/note-tag-editor.component';
import { LayoutService } from '../../../_service/layout.service';
import { AlertService } from '../../../_service/alert.service';
import { LogService } from '../../../_service/log.service';
import { FilterParams, FilterService } from '../../../_service/filter.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { NoteActionsService } from '../../../_service/note-actions.service';
import {
  BulkActionDialogComponent,
  BulkActionRequest,
} from '../bulk-action-dialog/bulk-action-dialog.component';
import {
  PAGE_SIZE_OPTIONS,
  PageResult,
  PageSize,
  clampPage,
  formatResultCount,
  isUnlimited,
  paginate,
  readStoredPageSize,
  rowsPerPage,
  writeStoredPageSize,
} from '../logic/pagination.logic';
import {
  isActionRequired,
  isEmphasizedStatus,
  isMutedStatus,
  isStruckStatus,
  priorityIndicator,
  priorityRank,
} from '../../../_util/note-status.util';
import { patchTableColumnResizeForZoom } from '../../../_util/table-resize-zoom.util';

type CardSort = {
  project_id: number;
  sort_order: number;
};

type TagDisplayItem = {
  name: string;
  color?: string;
  /** The current user's private tag (tasks/frontend-tagging.md) — shown with a lock. */
  isPrivate?: boolean;
};

const LIST_MOBILE_TABLE_STATE_KEY = 'notesapp.home.list.mobile.v1';
// v2: Priority and Action required columns added — saved column widths of the old layout no longer fit.
const LIST_DESKTOP_TABLE_STATE_KEY = 'notesapp.home.list.desktop.v2';
const LIST_DESKTOP_TABLE_STATE_KEY_V1 = 'notesapp.home.list.desktop.v1';
// Exported so the bulk "Export Notes..." action's "Default (as current view)"
// sort option can read the same persisted List column-sort without duplicating
// the storage key (see BulkActionsLogic.sortNoteIdsForExport).
export const LIST_SORT_STATE_KEY = 'notesapp.home.list.sort.v1';
export const LIST_SORT_STATE_DEFAULT: { sortBy: string; sortOrder: 'asc' | 'desc' } = {
  sortBy: 'Modified',
  sortOrder: 'desc',
};

/** The List view's persisted sort (what ListComponent.sortData() last stored), or the default. */
export function readListSortState(): { sortBy: string; sortOrder: 'asc' | 'desc' } {
  try {
    const raw = localStorage.getItem(LIST_SORT_STATE_KEY);
    if (!raw) return LIST_SORT_STATE_DEFAULT;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.sortBy === 'string' && (parsed?.sortOrder === 'asc' || parsed?.sortOrder === 'desc')) {
      return { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder };
    }
  } catch {
    // Ignore malformed/unavailable storage; fall back to the default below.
  }
  return LIST_SORT_STATE_DEFAULT;
}

// Columns that should default to descending order the first time they're clicked.
const DEFAULT_DESC_SORT_COLUMNS = new Set(['Modified', 'Size', 'Created', 'ReferenceDate', 'Priority', 'ActionRequired']);

/** A tag added to / removed from one note in the List (its Tags cell, or the phone's "Edit tags" dialog). */
export interface NoteTagChange {
  note: Note;
  tag: Tag;
  op: 'add' | 'remove';
}

@Component({
  selector: 'app-list',
  imports: [
    CommonModule,
    NoteDisplayTitlePipe,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    TableModule,
    CheckboxModule,
    PaginatorModule,
    PopoverModule,
    BulkActionDialogComponent,
    NoteTagEditorComponent,
  ],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListComponent implements OnInit, OnDestroy {
  readonly desktopTableStateKey = LIST_DESKTOP_TABLE_STATE_KEY;
  // Status styling and metadata indicators (tasks/frontend-note-status-metadata-requirements.md);
  // legacy statuses are styled like the status they map to.
  readonly isMutedStatus = isMutedStatus;
  readonly isStruckStatus = isStruckStatus;
  readonly isEmphasizedStatus = isEmphasizedStatus;
  readonly isActionRequired = isActionRequired;
  readonly priorityIndicator = priorityIndicator;

  // The mobile view no longer uses a <p-table> (see list.component.html — a plain card list now),
  // so there is no zoom-aware column-resize patch to apply there; only the desktop table needs it.
  private desktopTableRef?: Table;

  @ViewChild('desktopTable')
  set desktopTable(table: Table | undefined) {
    this.desktopTableRef = table;
    queueMicrotask(() => this.patchColumnResizeForZoom(table));
  }
  get desktopTable(): Table | undefined {
    return this.desktopTableRef;
  }

  /** The print-only sheet is moved to <body>, so a print shows just it (styles.scss hides the rest). */
  @ViewChild('printSheet')
  set printSheet(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement;
    if (el && typeof document !== 'undefined' && el.parentElement !== document.body) {
      this.printSheetEl = el;
      document.body.appendChild(el);
    }
  }
  private printSheetEl: HTMLElement | null = null;

  /** Tailwind's `lg` breakpoint (64rem). Decides which single list layout is rendered. */
  private static readonly LARGE_SCREEN_QUERY = '(min-width: 64rem)';
  isLargeScreen = true;
  private largeScreenMql?: MediaQueryList;
  private readonly onLargeScreenChange = (event: MediaQueryListEvent) => {
    this.isLargeScreen = event.matches;
    this.publishDesktopPager();
    this.cancelLongPress();
    this.closeNoteMenu();
    this.cdr.markForCheck(); // OnPush: a media-query change is not an input or DOM event of this component
  };
  @Input() notes: Note[] = [];
  @Input() tags: Tag[] = [];
  @Input() spaces: Space[] = [];
  /** The current user's private tag ids per note (from Home). Since the Phase 1 migration a note's
   * own Tags holds only Space tags, so the Tags column needs these too. */
  @Input() privateTagIndex: PrivateTagIndex = null;
  @Input() filter: FilterParams = {};
  /** Printing: a filter is on (title "Notes (filtered)") and its description, printed after the table. */
  @Input() isFiltered = false;
  @Input() printFilterSummary: Array<{ label: string; value: string }> = [];
  @Input() isSelectMode: boolean = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();
  /** Changing a note's tags from its Tags cell (or the phone's "Edit tags" dialog); Home applies it. */
  @Input() isOnline = true;
  @Output() noteTagChange = new EventEmitter<NoteTagChange>();
  @Output() tagCreated = new EventEmitter<Tag>();
  /** Desktop / iPad: the page navigation bar is shown by Home, in its fixed bottom area right on top of the
   * footer (full width, beside the "+" button), so it's handed over here; null when this layout doesn't use it
   * (phones show it at the top of the card list) or the list is gone. */
  @Output() desktopPagerChange = new EventEmitter<TemplateRef<unknown> | null>();
  @ViewChild('pager', { static: true }) private pagerTemplate?: TemplateRef<unknown>;


  data: Note[] = [];

  // ---- Pagination (Option A: in-memory; see docs/list-view-pagination-analysis.md) ----
  // Pipeline: notes (= ALL filtered notes, from Home) → sortedNotes (ALL of them, sorted) → pageItems (current page).
  // `sortedNotes` is always the COMPLETE sorted result; only `pageItems` is handed to the table. ListComponent
  // owns this state, and it is the single authoritative pagination state for both the mobile and desktop layouts.
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS.map((value) => ({
    value,
    label: isUnlimited(value) ? 'No limit' : String(value),
  }));
  /** Device-local preference (localStorage), validated on read; invalid or missing → 50. */
  pageSize: PageSize = readStoredPageSize(ListComponent.safeLocalStorage());
  /** 1-based. */
  currentPage = 1;
  /** The rows the table renders: exactly the current page of `sortedNotes`. */
  pageItems: Note[] = [];
  /** Size of the COMPLETE matching result (all pages). */
  totalRecords = 0;
  /** Last computed page (range, page count, offset) for the paginator and result text. */
  pageInfo: PageResult<Note> = paginate<Note>([], 1, this.pageSize);
  private lastQuerySignature: string | null = null;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  sortBy: string = 'Modified';
  sortOrder: 'asc' | 'desc' = 'desc';
  isResizing: boolean = false;
  //projects: Project[] | undefined;
  card_sort: CardSort[] = [];
  statusValues: string[] | undefined;
  types: string[] | undefined;
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  loadingCounter = 0; // initially count of loading objects in progress

  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  synchLog$: Observable<string[]>;

  /** IDs of notes the user has marked as favorite (user-settings), for the ♥ marker next to the title. */
  private favoriteNoteIds = new Set<string>();
  private favoriteNoteIdsSub?: Subscription;
  private noteSizeSub?: Subscription;
  /** Select mode's first column: the checkbox (20px) plus the cell padding, nothing more. */
  readonly selectColumnStyle = 'width: 2.25rem !important; min-width: 2.25rem !important; max-width: 2.25rem !important';
  /** Just wide enough for the Edit button (1rem icon + the 2px cell spacing on each side + borders). */
  readonly editColumnStyle = 'width: 1.75rem !important; min-width: 1.75rem !important; max-width: 1.75rem !important';
  private readonly noteSizes = inject(NoteSizeService);

  // ---- Mobile long-press note menu (tasks/mobile-long-press.md) ----
  // Touch/pen only, on the mobile card list only: a mouse never starts a long press, and the desktop
  // table has no handlers at all. Every action is also reachable from the View page's menu, so the
  // long press is a shortcut, never the only way to do something.
  private static readonly LONG_PRESS_MS = 550;
  /** Movement beyond this (px) means the user is scrolling/dragging, not pressing. */
  private static readonly LONG_PRESS_MOVE_TOLERANCE_PX = 10;
  /** How long after the finger lifts a stray click is still swallowed. */
  private static readonly CLICK_SUPPRESS_AFTER_RELEASE_MS = 400;
  private readonly noteActions = inject(NoteActionsService);
  private pendingPress: {
    pointerId: number;
    startX: number;
    startY: number;
    note: Note;
    row: HTMLElement;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  private releaseClickSuppression?: () => void;
  /** The row currently being pressed (light highlight while the finger is down). */
  pressedNoteId: string | null = null;
  noteMenuVisible = false;
  /** Invisible 1px-wide anchor the popover attaches to: at the finger's x, spanning the pressed row. */
  noteMenuAnchor = { left: 0, top: 0, height: 0 };
  @ViewChild('noteMenuPopover') private noteMenuPopover?: Popover;
  @ViewChild('noteMenuAnchorEl') private noteMenuAnchorEl?: ElementRef<HTMLElement>;
  /** The pressed row, re-measured whenever the menu is (re)positioned. */
  private noteMenuRow?: HTMLElement;
  /** Where the finger was (viewport x): the menu's arrow points there. */
  private noteMenuPressX = 0;
  /** Re-places the menu whenever its size settles/changes while open (first layout, Export list swap). */
  private noteMenuResizeObserver?: ResizeObserver;
  /** Gap between the row and the menu (room for the popover arrow). */
  private static readonly NOTE_MENU_GAP_PX = 10;
  /** Minimum distance the menu keeps from the viewport edges. */
  private static readonly NOTE_MENU_EDGE_PX = 8;
  noteMenuNote: Note | null = null;
  noteMenuItems: MenuItem[] = [];
  noteDocxExportVisible = false;
  private noteDocxExportTarget: Note | null = null;
  readonly noteDocxExportAction = NoteActionsService.DOCX_EXPORT_ACTION;

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private logs: LogService,
    private messages: MessageService,
    private userSettings: UserSettingsService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
  }

  ngOnInit(): void {
    document.addEventListener('click', this.onDocumentClickForTagCell, true);
    console.log('ListComponent - in ngOnInit()');
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.largeScreenMql = window.matchMedia(ListComponent.LARGE_SCREEN_QUERY);
      this.isLargeScreen = this.largeScreenMql.matches;
      this.largeScreenMql.addEventListener('change', this.onLargeScreenChange);
    }
    ListComponent.dropStalePagingFromTableState(LIST_MOBILE_TABLE_STATE_KEY);
    ListComponent.dropStalePagingFromTableState(LIST_DESKTOP_TABLE_STATE_KEY);
    try {
      localStorage.removeItem(LIST_DESKTOP_TABLE_STATE_KEY_V1); // column widths of the layout before Priority/Action required
    } catch {
      // Storage unavailable: nothing to clean up.
    }
    this.loadPersistedSort();
    // ngOnChanges (which seeds sortedNotes) runs before ngOnInit, using the
    // sortBy/sortOrder defaults — recompute now that the persisted sort is loaded.
    this.recomputeSortedNotes();

    this.favoriteNoteIdsSub = this.userSettings.userSettings$.subscribe((settings) => {
      const ids = Array.isArray(settings?.favoriteNoteIds) ? settings!.favoriteNoteIds! : [];
      this.favoriteNoteIds = new Set(ids.filter((id): id is string => typeof id === 'string'));
      // External async source under OnPush — mark for check so the ♥ markers refresh.
      this.cdr.markForCheck();
    });

    // Sizes include attachments, recounted asynchronously from the local attachment cache.
    this.noteSizeSub = this.noteSizes.changed$.subscribe(() => {
      if (this.sortBy === 'Size') {
        this.recomputeSortedNotes();
      }
      this.cdr.markForCheck();
    });
  }

  isFavoriteNote(note: Note): boolean {
    return !!note._id && this.favoriteNoteIds.has(note._id);
  }

  private loadPersistedSort(): void {
    try {
      const raw = localStorage.getItem(LIST_SORT_STATE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // 'ID' was a column once; a list sorted by it falls back to the default.
      if (typeof parsed?.sortBy === 'string' && parsed.sortBy !== 'ID' && (parsed?.sortOrder === 'asc' || parsed?.sortOrder === 'desc')) {
        this.sortBy = parsed.sortBy;
        this.sortOrder = parsed.sortOrder;
      }
    } catch {
      // Ignore malformed/unavailable storage; fall back to defaults.
    }
  }

  private persistSort(): void {
    try {
      localStorage.setItem(LIST_SORT_STATE_KEY, JSON.stringify({ sortBy: this.sortBy, sortOrder: this.sortOrder }));
    } catch {
      // Ignore storage failures (e.g. private browsing quota).
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.patchColumnResizeForZoom(this.desktopTable);
    });
    this.publishDesktopPager();
  }

  /** Out of the current change-detection pass, so Home's template can take it up on the next one. */
  private publishDesktopPager(gone = false): void {
    const pager = !gone && this.isLargeScreen ? this.pagerTemplate ?? null : null;
    queueMicrotask(() => this.desktopPagerChange.emit(pager));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // notes/tags drive both the tag-chip lookup and (via the Tags column) the
    // sort order, so keep both caches in lockstep whenever either input changes —
    // recomputing per-render (the old behavior) meant re-sorting the full list and
    // rebuilding a tag-id Map for every row on every single change-detection tick.
    // A changed search / filter / saved search / tag / category means a NEW result set: back to page 1.
    // A plain data refresh (sync, edits) with the same query keeps the current page (clamped if the result shrank).
    // Home mutates the SAME filter object for search-as-you-type, so compare a signature, not the reference.
    const queryChanged = this.hasQueryChanged();
    if (changes['spaces']) {
      this.rebuildSpaceNameCache();
      // Spaces can arrive slightly after notes (separate async fetch) — if the Space column is
      // what's currently sorted, re-sort once names are known rather than leaving it stuck
      // ordered by raw ids from before this cache existed.
      if (this.sortBy === 'Space') {
        this.recomputeSortedNotes();
      }
    }
    if (changes['notes'] || changes['tags'] || changes['privateTagIndex']) {
      this.rebuildTagDisplayCache();
      this.recomputeSortedNotes(queryChanged);
    } else if (queryChanged) {
      this.currentPage = 1;
      this.updatePage(true);
    }
  }

  ngOnDestroy() {
    this.publishDesktopPager(true);
    document.removeEventListener('click', this.onDocumentClickForTagCell, true);
    this.cancelLongPress();
    this.releaseClickSuppression?.();
    this.noteMenuResizeObserver?.disconnect();
    this.largeScreenMql?.removeEventListener('change', this.onLargeScreenChange);
    this.favoriteNoteIdsSub?.unsubscribe();
    this.noteSizeSub?.unsubscribe();
    this.printSheetEl?.remove();
  }

  private patchColumnResizeForZoom(table?: Table): void {
    patchTableColumnResizeForZoom(table);
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

      sortData(sortBy: string): void {
        // Prevent sorting if a column resize is in progress
        if (this.isResizing) {
          console.log('Skipping sort - resize in progress');
          return;
        }
        
        if (this.sortBy === sortBy) {
          this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortBy = sortBy;
          // Modified and Size columns default to descending, all others to ascending
          this.sortOrder = DEFAULT_DESC_SORT_COLUMNS.has(sortBy) ? 'desc' : 'asc';
        }
        this.persistSort();
        this.recomputeSortedNotes(true); // a new sort order starts again at the top of the result
      }
      
      onColumnResizeEnd(): void {
        // Set flag when resize happens
        this.isResizing = true;
        // Delay reset to ensure click event doesn't fire after resize
        setTimeout(() => {
          this.isResizing = false;
        }, 100);
      }
    



  toggleDarkMode() {
    this.layoutService.appState.update((state) => ({
      ...state,
      darkMode: !state.darkMode,
    }));
  }

  DEBUG_printAssignedSortOrders(projects: Project[] | undefined, card_sort: CardSort[]) {
    //console.log("Assigned Sort Orders:");
    card_sort.forEach((card) => {
      //   const project = projects.find(p => p.ID === card.project_id);
      const project = (projects ?? []).find((p) => p.ID === card.project_id);
      if (project) {
        console.log(`Project: ${project.Name}, Sort Order: ${card.sort_order}`);
      } else {
        console.log(`Project not found for project_id: ${card.project_id}`);
      }
    });
  }

  assignSortOrderToProjects(
    projects: Project[] | undefined,
    noteItems: Note[],
    card_sort: CardSort[],
  ) {
    // First, filter out the projects that already have a sort order assigned in card_sort
    const projectHasSort = new Set(card_sort.map((cs) => cs.project_id));
    // console.log(`projectHasSort: ${[...projectHasSort]}`);

    // Filter note-items that belong to projects that haven't been sorted yet
    const unsortedNoteItems = noteItems.filter((note) => !projectHasSort.has(note.Project));

    // Sort the note-items by last_modified in descending order (latest first)
    const sortedNoteItems = unsortedNoteItems.sort((a, b) => {
      return new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime();
    });

    // Start assigning sort orders, beginning with the value after the last assigned sort order
    let nextSortValue = card_sort.length + 1;

    // Track projects that have been assigned a sort order in this function call
    const assignedProjects = new Set<number>();

    // Assign sort order based on the sorted note-items
    sortedNoteItems.forEach((note) => {
      const project = (projects ?? []).find((p) => p.ID === note.Project);

      if (project && !projectHasSort.has(project.ID) && !assignedProjects.has(project.ID)) {
        // Add the project to the card_sort with the next sort value
        card_sort.push({ project_id: project.ID, sort_order: nextSortValue });
        nextSortValue++;
        // Mark the project as assigned
        assignedProjects.add(project.ID);
      }
    });

    // Optionally: sort the card_sort array by sort_order if needed
    card_sort.sort((a, b) => a.sort_order - b.sort_order);
  }

  sortNotesByCardSort(): void {
    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    this.card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the data array based on the sort_order in card_sort
    this.data.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.Project) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.Project) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });

    // Update paginatedData after sorting
    //this.paginateData();
  }

  //   sortProjectsByCardSort(): void {
  //     // Create a map from project_id to sort_order for quick lookup
  //     const sortOrderMap = new Map<number, number>();
  //     this.card_sort.forEach((card) => {
  //       sortOrderMap.set(card.project_id, card.sort_order);
  //     });

  //     // Sort the projects array based on the sort_order in card_sort
  //     this.projects?.sort((a, b) => {
  //       const sortOrderA = sortOrderMap.get(a.ID) ?? Number.MAX_SAFE_INTEGER;
  //       const sortOrderB = sortOrderMap.get(b.ID) ?? Number.MAX_SAFE_INTEGER;
  //       return sortOrderA - sortOrderB;
  //     });
  //   }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // If current day is less than the modified day, subtract one (not a full month yet)
    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

  viewAllNotes(projectId: number): void {
    // this.router.navigate(['/project', projectId]);
  }

  toggleHeaderVisibility(): void {
    //console.log("toggle header now =",!this.isHeaderVisible);
    this.isHeaderVisible = !this.isHeaderVisible;
  }

  clickRow(row: Note) {
    if (this.isSelectMode) {
      this.toggleSelection(row);
      return;
    }

    console.log('clickRow() - Note: ', row.Title);
    this.navigateToView(row);
  }

  // ##############################################################################
  // TAGS CELL: ADD / REMOVE A NOTE'S TAGS IN PLACE
  // ##############################################################################

  /** The note whose desktop Tags cell is selected (showing the tag editor), if any. */
  tagEditNoteId: string | null = null;
  /** Phone: the note whose "Edit tags" dialog is open (looked up by id, so it follows Home's updates). */
  private tagDialogNoteId: string | null = null;

  get tagDialogNote(): Note | undefined {
    return this.tagDialogNoteId ? this.notes.find((note) => note._id === this.tagDialogNoteId) : undefined;
  }

  privateTagIdsFor(note: Note): readonly string[] {
    return this.privateTagIndex?.get(note._id) ?? [];
  }

  /** A click in a Tags cell only selects it (never opens the note). In Select mode it selects the row as usual. */
  onTagsCellClick(event: MouseEvent, note: Note): void {
    if (this.isSelectMode) return;
    event.stopPropagation();
    this.tagEditNoteId = note._id;
    this.cdr.markForCheck();
  }

  openTagDialog(note: Note): void {
    this.tagDialogNoteId = note._id;
    this.cdr.markForCheck();
  }

  closeTagEditor(): void {
    if (!this.tagEditNoteId && !this.tagDialogNoteId) return;
    this.tagEditNoteId = null;
    this.tagDialogNoteId = null;
    this.cdr.markForCheck();
  }

  /** A click outside the selected Tags cell unselects it and does nothing else (like closing a menu):
   * listened to in the capture phase, so the row, link or button under the pointer never sees it.
   * Clicks in its "Assign tag…" suggestions (shown in <body>) count as inside. OutsideClickGuard's stand-in
   * click (untrusted — when the click only closed an open menu) unselects it but isn't stopped, so
   * the menu still closes. */
  private readonly onDocumentClickForTagCell = (event: MouseEvent): void => {
    if (!this.tagEditNoteId) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.note-tags-cell--editing, .p-autocomplete-overlay, .p-toast')) return;
    if (event.isTrusted) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeTagEditor();
  };

  // ##############################################################################
  // MOBILE LONG-PRESS NOTE MENU
  // ##############################################################################

  onRowPointerDown(event: PointerEvent, note: Note): void {
    this.releaseClickSuppression?.(); // a new press: any earlier long press is over
    this.cancelLongPress();
    if (this.isSelectMode || event.pointerType === 'mouse' || !event.isPrimary || event.button > 0) {
      return;
    }
    this.pressedNoteId = note._id;
    this.pendingPress = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      note,
      row: event.currentTarget as HTMLElement,
      timer: setTimeout(() => this.fireLongPress(), ListComponent.LONG_PRESS_MS),
    };
  }

  onRowPointerMove(event: PointerEvent): void {
    const press = this.pendingPress;
    if (!press || event.pointerId !== press.pointerId) return;
    const dx = event.clientX - press.startX;
    const dy = event.clientY - press.startY;
    if (Math.hypot(dx, dy) > ListComponent.LONG_PRESS_MOVE_TOLERANCE_PX) {
      this.cancelLongPress();
    }
  }

  /** pointerup / pointercancel / pointerleave (the browser fires pointercancel once it starts scrolling). */
  onRowPointerEnd(event: PointerEvent): void {
    if (this.pendingPress && event.pointerId === this.pendingPress.pointerId) {
      this.cancelLongPress();
    }
  }

  /**
   * Android raises `contextmenu` for a touch long press (and iOS may show its own callout). Suppress
   * the native menu ONLY while one of our touch presses is in progress / has just fired — a mouse
   * right-click keeps the browser's normal menu.
   */
  onRowContextMenu(event: Event): void {
    if (this.pendingPress) {
      event.preventDefault();
      this.fireLongPress(); // the platform decided it was a long press: open ours now
    } else if (this.releaseClickSuppression) {
      event.preventDefault();
    }
  }

  private fireLongPress(): void {
    const press = this.pendingPress;
    if (!press) return;
    this.cancelLongPress();
    this.suppressClickAfterLongPress();
    try {
      navigator.vibrate?.(10); // subtle haptic tick where supported (Android); no-op elsewhere
    } catch {
      /* ignore */
    }
    this.openNoteMenu(press.note, press.row, press.startX);
  }

  private cancelLongPress(): void {
    if (this.pendingPress) {
      clearTimeout(this.pendingPress.timer);
      this.pendingPress = null;
    }
    if (this.pressedNoteId !== null) {
      this.pressedNoteId = null;
      this.cdr.markForCheck();
    }
  }

  /**
   * Once the long press fired, the finger's release must not ALSO open the note (row click) or
   * immediately dismiss the menu that just appeared under it (mask click). A capture-phase listener
   * swallows clicks until shortly after the finger lifts.
   */
  private suppressClickAfterLongPress(): void {
    this.releaseClickSuppression?.();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const swallowClick = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onRelease = () => {
      clearTimeout(timer);
      timer = setTimeout(release, ListComponent.CLICK_SUPPRESS_AFTER_RELEASE_MS);
    };
    const release = () => {
      clearTimeout(timer);
      document.removeEventListener('click', swallowClick, true);
      document.removeEventListener('pointerup', onRelease, true);
      document.removeEventListener('pointercancel', onRelease, true);
      if (this.releaseClickSuppression === release) this.releaseClickSuppression = undefined;
    };
    document.addEventListener('click', swallowClick, true);
    document.addEventListener('pointerup', onRelease, true);
    document.addEventListener('pointercancel', onRelease, true);
    // Safety net: never leave clicks blocked if no release event ever arrives.
    timer = setTimeout(release, 5000);
    this.releaseClickSuppression = release;
  }

  @HostListener('document:visibilitychange')
  @HostListener('window:blur')
  onPageHidden(): void {
    this.cancelLongPress();
  }

  /**
   * Opens the action menu right next to the pressed note — below the row, or above it when there is
   * not enough room below (Popover flips itself) — with its arrow pointing at where the finger was.
   */
  openNoteMenu(note: Note, row?: HTMLElement, pressX?: number): void {
    const rect = row?.getBoundingClientRect();
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const x = pressX ?? (rect ? rect.left + rect.width / 2 : viewportWidth / 2);
    // Popover left-aligns with its anchor and only shifts back when it overflows the right edge, so
    // keep the anchor a little in from both edges.
    this.noteMenuAnchor = {
      left: Math.min(Math.max(x, 16), Math.max(16, viewportWidth - 16)),
      top: rect ? rect.top : (typeof window !== 'undefined' ? window.innerHeight / 3 : 0),
      height: rect ? rect.height : 1,
    };
    this.noteMenuRow = row;
    this.noteMenuPressX = x;
    this.noteMenuNote = note;
    this.noteMenuItems = this.buildNoteMenuItems(note);
    this.noteMenuVisible = true;
    this.cdr.detectChanges(); // the anchor must be at its new position before the popover measures it
    const anchor = this.noteMenuAnchorEl?.nativeElement;
    if (anchor) this.noteMenuPopover?.show(null, anchor);
  }

  closeNoteMenu(): void {
    this.noteMenuPopover?.hide();
    this.onNoteMenuHidden();
  }

  /**
   * Final placement, applied after Popover's own alignment (on show, and again when the menu swaps to
   * the taller Export list). Vertically: below the row when it fits, else above it, else on whichever
   * side has more room, clamped inside the viewport (the menu then scrolls internally and loses its
   * arrow, since it no longer sits against the row) — Popover's default can end up partly off-screen.
   * Horizontally: starting just left of the finger, kept inside the viewport, arrow pointing at the finger.
   * Measured with offset* sizes, which ignore the open animation's scale transform.
   */
  positionNoteMenu(): void {
    const popover = this.noteMenuPopover?.container as HTMLElement | undefined;
    const row = this.noteMenuRow;
    if (!popover || !row?.isConnected || typeof window === 'undefined') return;
    const gap = ListComponent.NOTE_MENU_GAP_PX;
    const edge = ListComponent.NOTE_MENU_EDGE_PX;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const rowRect = row.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;
    const spaceBelow = viewportHeight - rowRect.bottom - gap - edge;
    const spaceAbove = rowRect.top - gap - edge;

    let top: number;
    let placement: 'below' | 'above' | 'overlap';
    if (height <= spaceBelow) {
      top = rowRect.bottom + gap;
      placement = 'below';
    } else if (height <= spaceAbove) {
      top = rowRect.top - gap - height;
      placement = 'above';
    } else {
      top = spaceBelow >= spaceAbove ? rowRect.bottom + gap : rowRect.top - gap - height;
      top = Math.min(Math.max(top, edge), Math.max(edge, viewportHeight - edge - height));
      placement = 'overlap';
    }

    // Popover's arrow tip sits at (arrow offset + --p-popover-arrow-left) from the menu's left edge.
    const arrowOffset = 20; // Aura `popover.arrow.offset` (1.25rem)
    const left = Math.min(
      Math.max(this.noteMenuPressX - arrowOffset - 12, edge),
      Math.max(edge, viewportWidth - edge - width),
    );
    const arrowLeft = Math.min(Math.max(this.noteMenuPressX - left - arrowOffset, 0), Math.max(0, width - 2 * arrowOffset));

    // The container is absolutely positioned in document coordinates: shift by the on-screen delta
    // (offsetTop/offsetLeft are relative to <body>, the popover being appended there).
    const styleTop = parseFloat(popover.style.top || '0') || 0;
    const styleLeft = parseFloat(popover.style.left || '0') || 0;
    popover.style.top = `${styleTop + (top - (popover.offsetTop - window.scrollY))}px`;
    popover.style.left = `${styleLeft + (left - (popover.offsetLeft - window.scrollX))}px`;
    popover.style.setProperty('--p-popover-arrow-left', `${arrowLeft}px`);
    popover.classList.toggle('p-popover-flipped', placement === 'above');
    popover.classList.toggle('note-action-popover--no-arrow', placement === 'overlap');
    if (placement === 'above') popover.setAttribute('data-p-popover-flipped', 'true');
    else popover.removeAttribute('data-p-popover-flipped');
  }

  onNoteMenuShown(): void {
    this.positionNoteMenu();
    const popover = this.noteMenuPopover?.container as HTMLElement | undefined;
    if (popover && typeof ResizeObserver !== 'undefined') {
      this.noteMenuResizeObserver?.disconnect();
      this.noteMenuResizeObserver = new ResizeObserver(() => this.positionNoteMenu());
      this.noteMenuResizeObserver.observe(popover);
    }
    requestAnimationFrame(() => this.positionNoteMenu());
  }

  /** The popover closed itself (outside tap, scroll, Escape) or was closed by an action. */
  onNoteMenuHidden(): void {
    this.noteMenuResizeObserver?.disconnect();
    this.noteMenuResizeObserver = undefined;
    if (!this.noteMenuVisible && !this.noteMenuNote) return;
    this.noteMenuVisible = false;
    this.noteMenuNote = null;
    this.noteMenuRow = undefined;
    this.cdr.markForCheck();
  }

  private buildNoteMenuItems(note: Note): MenuItem[] {
    // Close the menu first, then act — so a following dialog/navigation never stacks on top of it.
    const run = (action: () => void) => () => {
      this.closeNoteMenu();
      action();
    };
    // Kept flat (no item groups): p-menu renders every top-level entry as a non-clickable group
    // label as soon as any of them has children. Export therefore swaps the menu's content to
    // the format list (with a Back entry) instead of nesting.
    return [
      { label: 'View', icon: 'pi pi-eye', command: run(() => this.navigateToView(note)) },
      { label: 'Edit', icon: 'pi pi-pencil', command: run(() => this.navigateToEdit(note)) },
      { label: 'Edit tags', icon: 'pi pi-tags', command: run(() => this.openTagDialog(note)) },
      {
        label: 'Export',
        icon: 'pi pi-download',
        state: { submenu: true },
        command: () => this.showNoteMenuItems(this.buildNoteExportItems(note, run)),
      },
      { separator: true },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        state: { danger: true },
        command: run(() =>
          this.noteActions.confirmAndDelete(note, () => {
            if (note._id && this.isNoteSelected(note._id)) {
              this.selectedNoteIdsChange.emit(this.selectedNoteIds.filter((id) => id !== note._id));
            }
            // DataService.deleteNote only touches IndexedDB/remote: reload memory so Home re-filters.
            void this.dataService.refreshLocalData();
          }),
        ),
      },
    ];
  }

  /** Same formats as the View page's Export submenu, run through the same NoteActionsService. */
  private buildNoteExportItems(note: Note, run: (action: () => void) => () => void): MenuItem[] {
    return [
      {
        label: 'Back',
        icon: 'pi pi-arrow-left',
        state: { muted: true },
        command: () => this.showNoteMenuItems(this.buildNoteMenuItems(note)),
      },
      { separator: true },
      {
        label: 'Export PDF (without images)',
        icon: 'pi pi-file-pdf',
        command: run(() => void this.noteActions.exportToPdf(note)),
      },
      {
        label: 'Export PDF (with images)',
        icon: 'pi pi-images',
        command: run(() => void this.noteActions.exportToPdf(note, { includeAttachments: true })),
      },
      {
        label: 'Export to MS Word (.docx)',
        icon: 'pi pi-file-word',
        command: run(() => this.openDocxExport(note)),
      },
      {
        label: 'Export to JSON file',
        icon: 'pi pi-file',
        command: run(() => void this.noteActions.exportToJson(note)),
      },
    ];
  }

  private showNoteMenuItems(items: MenuItem[]): void {
    this.noteMenuItems = items;
    this.cdr.detectChanges(); // render the new list now so its height can be measured
    this.positionNoteMenu();
  }

  private openDocxExport(note: Note): void {
    this.noteDocxExportTarget = note;
    this.noteDocxExportVisible = true;
    this.cdr.markForCheck();
  }

  onNoteDocxExportCancel(): void {
    this.noteDocxExportVisible = false;
    this.noteDocxExportTarget = null;
  }

  async onNoteDocxExportSubmit(request: BulkActionRequest): Promise<void> {
    const note = this.noteDocxExportTarget;
    this.noteDocxExportVisible = false;
    this.noteDocxExportTarget = null;
    if (request.action !== 'export-docx' || !note) return;
    await this.noteActions.exportToDocx(note, request.params);
  }

  toggleSelection(note: Note, event?: MouseEvent | boolean) {
    if (event && typeof event === 'object' && 'stopPropagation' in event) {
      event.stopPropagation();
    }
    console.log('toggleSelection() - Note: ', note.Title);
    const id = note._id;
    if (!id) {
      return;
    }

    const isAlreadySelected = this.isNoteSelected(id);
    const updatedSelection = isAlreadySelected
      ? this.selectedNoteIds.filter((n) => n !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(updatedSelection);
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  onHeaderDoubleClick(sortBy: string): void {
    this.sortBy = sortBy;
    this.sortOrder = 'asc';
    this.persistSort();
    this.recomputeSortedNotes(true);
  }

  /** Cached result of the sort below — bound directly in the template instead of
   * a getter. A getter re-sorts the entire (potentially large) note list on every
   * Angular change-detection pass that touches this component, which under default
   * change detection could mean every click anywhere in the app, not just here. */
  sortedNotes: Note[] = [];

  /** SORT step: sorts a copy of ALL filtered notes, then derives the current page from the complete result. */
  private recomputeSortedNotes(resetPage = false): void {
    this.sortedNotes = this.computeSortedNotes();
    if (resetPage) this.currentPage = 1;
    this.updatePage(resetPage);
  }

  private computeSortedNotes(): Note[] {
    const notes = [...(this.notes || [])];
    if (!this.sortBy || this.sortBy === 'Link') {
      return notes;
    }

    const dir = this.sortOrder === 'desc' ? -1 : 1;

    if (this.sortBy === 'Size') {
      return notes.sort((a, b) => dir * (this.noteSizes.sortableSize(a) - this.noteSizes.sortableSize(b)));
    }
    // Ties keep the incoming (filtered) order: Array.prototype.sort is stable.
    if (this.sortBy === 'Priority') {
      return notes.sort((a, b) => dir * (priorityRank(a.priority) - priorityRank(b.priority)));
    }
    if (this.sortBy === 'ActionRequired') {
      return notes.sort((a, b) => dir * (Number(isActionRequired(a)) - Number(isActionRequired(b))));
    }

    const getVal = (note: Note, key: string): string => {
      switch (key) {
        case 'Tags':
          return this.getTagNames(note).toLowerCase();
        case 'Space':
          return this.getSpaceName(note).toLowerCase();
        case 'Title':
          return (note.Title || '').toLowerCase();
        case 'Modified':
          return note.last_modified || '';
        case 'Created':
          return note.created_on || '';
        case 'ReferenceDate':
          return note.ReferenceDate || note.created_on || '';
        case 'Status':
          return (note.Status || '').toLowerCase();
        case 'Type':
          return (note.NoteType || '').toLowerCase();
        case 'ID':
          return (note._id || '').toLowerCase();
        default:
          return '';
      }
    };

    return notes.sort((a, b) => {
      const aVal = getVal(a, this.sortBy);
      const bVal = getVal(b, this.sortBy);
      return dir * aVal.localeCompare(bVal);
    });
  }

  // ##############################################################################
  // PAGINATION (PAGINATE step: only ever applied to the complete, already sorted result)
  // ##############################################################################

  /** Derives `pageItems` from the COMPLETE `sortedNotes`. `scrollToTop` is set only for explicit user changes. */
  private updatePage(scrollToTop = false): void {
    this.pageInfo = paginate(this.sortedNotes, this.currentPage, this.pageSize);
    this.currentPage = this.pageInfo.page; // clamped: never leave the list blank because the page no longer exists
    this.pageItems = this.pageInfo.items;
    this.totalRecords = this.pageInfo.totalRecords;
    if (scrollToTop) {
      this.scrollListToTop();
    }
    this.cdr.markForCheck();
  }

  /** PrimeNG paginator → our state (the paginator is a view of it, not a second source of truth). */
  onPageChange(event: PaginatorState): void {
    const requested = (event.page ?? 0) + 1;
    this.currentPage = clampPage(requested, this.sortedNotes.length, this.pageSize);
    this.updatePage(true);
  }

  onPageSizeChange(size: PageSize): void {
    if (size === this.pageSize) return;
    this.pageSize = size;
    writeStoredPageSize(ListComponent.safeLocalStorage(), size); // persisted immediately; no Save button
    this.currentPage = 1;
    this.updatePage(true);
  }

  /** Rows per page as the paginator wants it (No limit = one page holding everything). */
  get paginatorRows(): number {
    return rowsPerPage(this.pageSize, this.sortedNotes.length);
  }

  get paginatorFirst(): number {
    return this.pageInfo.first;
  }

  get resultCountLabel(): string {
    return formatResultCount(this.pageInfo);
  }

  /** Stable row identity so unchanged notes keep their DOM rows across sorts, refreshes and page changes. */
  readonly trackByNoteId = (_index: number, note: Note): string => note._id;

  /** True when the search/filter (not the data) differs from the last time we looked. */
  private hasQueryChanged(): boolean {
    let signature: string;
    try {
      signature = JSON.stringify(this.filter ?? {});
    } catch {
      signature = String(Math.random()); // unserialisable filter: treat as changed rather than risk a stale page
    }
    const changed = this.lastQuerySignature !== null && this.lastQuerySignature !== signature;
    this.lastQuerySignature = signature;
    return changed;
  }

  /** Scrolls only the list's own scroll container (never the window or the header). */
  private scrollListToTop(): void {
    requestAnimationFrame(() => {
      let el: HTMLElement | null = this.host.nativeElement.parentElement;
      while (el) {
        const overflowY = getComputedStyle(el).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          el.scrollTop = 0;
          return;
        }
        el = el.parentElement;
      }
    });
  }

  private static safeLocalStorage(): Storage | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null;
    }
  }

  /**
   * PrimeNG only saves/restores `first`/`rows` in table state when the table's OWN paginator is enabled, and this list
   * does not enable it (the standalone paginator above the table is driven by the state in this component). Remove any
   * stale paging left in the saved table state anyway, so it can never conflict with the single authoritative state.
   */
  private static dropStalePagingFromTableState(key: string): void {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (state && typeof state === 'object' && ('first' in state || 'rows' in state)) {
        delete state.first;
        delete state.rows;
        localStorage.setItem(key, JSON.stringify(state));
      }
    } catch {
      // Unreadable state is ignored: PrimeNG handles its own parsing errors.
    }
  }

  /** Note + attachments, human-readable (bytes / KB / MB) — as in View's Technical details, but whole KB below 1 MB. */
  getNoteSize(note: Note): string {
    const bytes = this.noteSizes.totalSizeBytes(note);
    return bytes === undefined ? '—' : formatPersistedNoteSize(bytes, { wholeBelowMb: true });
  }

  /** note._id → resolved Space name (tasks/space_ux.md), rebuilt only when `spaces` changes —
   * same reasoning as tagDisplayCache below: avoid an O(spaces) lookup per row per CD pass. */
  private spaceNameById = new Map<string, string>();

  private sharedSpaceIds = new Set<string>();

  private rebuildSpaceNameCache(): void {
    this.spaceNameById = new Map((this.spaces || []).map((space) => [space.id, space.name]));
    this.sharedSpaceIds = new Set((this.spaces || []).filter((space) => space.type === 'SHARED').map((space) => space.id));
  }

  /** True until the note's last local change (edit, create or delete) has been synced to the server. */
  isNotSynced(note: Note): boolean {
    return !!note.syncStatus && note.syncStatus !== 'synced';
  }

  syncPendingLabel(note: Note): string {
    return note.syncStatus === 'deleted' ? 'Deletion not yet synced to server' : 'Not yet synced to server';
  }

  isInSharedSpace(note: Note): boolean {
    return !!note.spaceId && this.sharedSpaceIds.has(note.spaceId);
  }

  getSpaceName(note: Note): string {
    if (!note.spaceId) return 'Private';
    return this.spaceNameById.get(note.spaceId) ?? note.spaceId;
  }

  getTagNames(note: Note): string {
    return this.getTagDisplayItems(note)
      .map((tag) => tag.name)
      .join(', ');
  }

  /** Cache of note._id → resolved tag chips, rebuilt only when notes/tags actually
   * change (see ngOnChanges/rebuildTagDisplayCache). Previously this rebuilt a
   * Map of every tag in the system, per note row, on every change-detection pass —
   * O(rows × tags) work redone dozens of times a minute as the note/tag count grew. */
  private tagDisplayCache = new Map<string, TagDisplayItem[]>();

  private rebuildTagDisplayCache(): void {
    this.tagDisplayCache = new Map<string, TagDisplayItem[]>();
    if (!this.tags?.length || !this.notes?.length) {
      return;
    }

    const byId = new Map(this.tags.map((tag) => [tag._id, tag]));

    for (const note of this.notes) {
      if (!note._id) {
        continue;
      }
      // Space tags (Note.Tags — or, on data the Phase 1 migration hasn't processed, the user's own
      // tags), then the user's private tags on the note; a tag in both is shown once.
      const refs: Array<string | Tag> = [...(note.Tags ?? []), ...(this.privateTagIndex?.get(note._id) ?? [])];
      if (!refs.length) {
        continue;
      }

      const seen = new Set<string>();
      const items = refs
        .map((tag) => {
          const resolvedTag = typeof tag === 'string' ? byId.get(tag) : byId.get(tag._id ?? '') ?? tag;

          if (!resolvedTag?.name || seen.has(resolvedTag._id)) {
            return null;
          }
          seen.add(resolvedTag._id);

          return {
            name: resolvedTag.name,
            color: resolvedTag.color,
            isPrivate: isPrivateTag(resolvedTag),
          } as TagDisplayItem;
        })
        .filter((tag): tag is TagDisplayItem => !!tag);

      if (items.length) {
        this.tagDisplayCache.set(note._id, items);
      }
    }
  }

  getTagDisplayItems(note: Note): TagDisplayItem[] {
    if (!note._id) {
      return [];
    }
    return this.tagDisplayCache.get(note._id) ?? [];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  // ---- Print ----

  /** Under the printed title: which notes (the page on screen), and when. */
  get printSummary(): string {
    const parts = [this.resultCountLabel];
    if (this.pageInfo.totalPages > 1) parts.push(`page ${this.pageInfo.page} of ${this.pageInfo.totalPages}`);
    return parts.join(' · ');
  }

  get printedAt(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Printed priority: only the non-default ones, as a word (the on-screen arrow icons don't print well). */
  printPriority(note: Note): string {
    const p = priorityIndicator(note.priority);
    return p.icon ? p.label.replace(/ priority$/, '') : '';
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return (
      element.offsetParent !== null && style.visibility !== 'hidden' && style.display !== 'none'
    );
  }

  // ##############################################################################
  // NAVIGATION FUNCTIONS
  // ##############################################################################

  goToListView(): void {
    console.log('goToListView');
    this.router.navigate(['/list'], { state: { filter: this.filter } });
  }

  navigateToView(row: Note) {
    this.router.navigate(['view', row._id], {
      state: { filter: this.filter },
    });
  }

  navigateToEdit(row: Note) {
    console.log(row);
    this.router.navigate(['edit', row._id], {
      state: { filter: this.filter },
    });
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################
}
